import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import config from "../config";

const PasswordResetPage = ({ accountType = "caregiver", loginPath = "/realMap" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = window.setInterval(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const requestCode = async () => {
    const response = await axios.post(`${config.API_BASE_URL}/auth/forgot-password`, { email, accountType });
    setStep("code");
    setResendCooldown(60);
    setMessage(response.data.message);
  };

  const resendCode = async () => {
    if (resendCooldown || loading) return;
    setLoading(true); setError(""); setMessage("");
    try { await requestCode(); }
    catch (requestError) { setError(requestError.response?.data?.error || "Unable to resend the verification code."); }
    finally { setLoading(false); }
  };

  const submit = async (event) => {
    event.preventDefault(); setError(""); setMessage("");
    if (step === "password" && password !== confirmPassword) return setError("Password and confirmation must match.");
    setLoading(true);
    try {
      if (step === "email") {
        await requestCode();
      } else if (step === "code") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/verify-reset-code`, { email, code, accountType });
        setResetToken(response.data.resetToken);
        setStep("password");
        setMessage(response.data.message);
      } else if (step === "password") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/reset-password`, { resetToken, password, accountType });
        setPassword(""); setConfirmPassword(""); setResetToken(""); setStep("done"); setMessage(response.data.message);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to complete password recovery.");
    } finally { setLoading(false); }
  };

  const titles = { email: "Forgot your password?", code: "Enter your verification code", password: "Choose a new password", done: "Password reset complete" };
  const descriptions = {
    email: "Enter the email linked to your caregiver profile. We’ll send a 6-digit code that expires in 10 minutes.",
    code: `Enter the code sent to ${email}.`,
    password: "Create a new password with at least 8 characters.",
    done: "You can now unlock your caregiver profile with the new password."
  };

  return <div style={page}><form style={card} onSubmit={submit}>
    <div style={badge}>Account recovery</div>
    <h1>{titles[step]}</h1>
    <p style={copy}>{descriptions[step]}</p>
    {message && <div style={successBox}>{message}</div>}{error && <div style={errorBox}>{error}</div>}

    {step === "email" && <input style={input} type="email" autoComplete="email" placeholder="Email address" required value={email} onChange={(event) => setEmail(event.target.value)} />}

    {step === "code" && <>
      <input style={{ ...input, textAlign: "center", fontSize: 24, letterSpacing: 8 }} type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} placeholder="000000" required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} />
      <button type="button" style={textButton} disabled={Boolean(resendCooldown) || loading} onClick={resendCode}>{resendCooldown ? `Resend code in ${resendCooldown}s` : "Resend code"}</button>
    </>}

    {step === "password" && <>
      <div style={{ position: "relative" }}><input style={{ ...input, paddingRight: 52 }} type={showPasswords ? "text" : "password"} autoComplete="new-password" placeholder="New password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /><button type="button" style={eyeButton} aria-label={showPasswords ? "Hide passwords" : "Show passwords"} onClick={() => setShowPasswords((shown) => !shown)}>{showPasswords ? <FaEyeSlash /> : <FaEye />}</button></div>
      <input style={input} type={showPasswords ? "text" : "password"} autoComplete="new-password" placeholder="Confirm new password" minLength={8} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
    </>}

    <div style={actions}>
      {step === "code" ? <button type="button" style={secondary} onClick={() => { setStep("email"); setCode(""); setMessage(""); setError(""); }}>Change email</button> : <button type="button" style={secondary} onClick={() => navigate(loginPath)}>Back to map</button>}
      {step !== "done" && <button type="submit" style={primary} disabled={loading}>{loading ? "Please wait…" : step === "email" ? "Send code" : step === "code" ? "Verify code" : "Reset password"}</button>}
    </div>
  </form></div>;
};

const page={minHeight:"70vh",display:"grid",placeItems:"center",padding:24};
const card={width:"min(520px,100%)",padding:32,borderRadius:24,background:"#fff",boxShadow:"0 20px 55px rgba(20,50,76,.14)",display:"grid",gap:16};
const badge={fontSize:12,fontWeight:900,textTransform:"uppercase",letterSpacing:1.2,color:"#237a70"};
const copy={color:"#5f6c76",lineHeight:1.6}; const input={width:"100%",boxSizing:"border-box",padding:"14px 16px",border:"1px solid #cbd5df",borderRadius:12,fontSize:16};
const actions={display:"flex",gap:12,justifyContent:"flex-end",flexWrap:"wrap"}; const primary={padding:"12px 18px",border:0,borderRadius:12,background:"#237a70",color:"#fff",fontWeight:800}; const secondary={...primary,background:"#e8eef2",color:"#14324c"};
const eyeButton={position:"absolute",right:16,top:"50%",transform:"translateY(-50%)",border:0,background:"transparent",color:"#5f6c76"}; const textButton={border:0,background:"transparent",color:"#237a70",fontWeight:800,justifySelf:"start",padding:0}; const successBox={padding:12,borderRadius:10,background:"#dcfce7",color:"#166534"}; const errorBox={...successBox,background:"#fee2e2",color:"#991b1b"};
export default PasswordResetPage;
